import React, { useContext, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import SelectPayrollTemplate from "../../components/select/SelectPayrollTemplate";
import { Eye, EyeOff, Trash2, FileDown, ArrowRight } from "lucide-react";
import PayrollExcelExporter from "../../helper/PayrollExcelExporter";
import swal from "../../services/swalConfig";
import GlobalContext from "../../context/GlobalContext";
import ModalAssignItemsPayroll from "../../components/modal/ModalAssignItemsPayroll";
import {
  savePayroll,
  calculatePayroll,
  getPayrollDetail,
  downloadPayrollSingle,
  publishPayrollSingle,
} from "../../api/PayrollApi";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

import { tr } from "../../i18n";
// Format using local date components (not toISOString) to avoid the UTC
// timezone shift that pushes the date back a day in UTC+ zones like WIB.
const formatLocalDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLast30DaysRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 29);
  return { from: formatLocalDate(from), to: formatLocalDate(today) };
};

export default function FormPayroll() {
  const { from: defaultFrom, to: defaultTo } = getLast30DaysRange();
  const methods = useForm({
    defaultValues: {
      payroll_name: "",
      from_date: defaultFrom,
      to_date: defaultTo,
      schema_option: "default",
      payroll_template_id: null,
      approved_by: "",
      overtime_from_excess_hours: false,
    },
  });

  const navigate = useNavigate();

  const { id } = useParams();
  const [payrollStatus, setPayrollStatus] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [schemaOption, setSchemaOption] = useState("default");
  const [showResults, setShowResults] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [loading, setLoadingProcess] = useState(false);
  const [error, setError] = useState(null);
  // Karyawan aktif yang tidak ikut dihitung karena belum punya skema payroll.
  const [skippedWithoutSchema, setSkippedWithoutSchema] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { user, setLoading } = useContext(GlobalContext);
  const [assignedItems, setAssignedItems] = useState([]);
  const companyID = user.workspace.company_id;

  // Tambahkan useEffect ini setelah deklarasi semua state
  useEffect(() => {
    if (id) {
      loadPayrollDetail(id);
    }
  }, [id]);

  // --- Fungsi Download Zip ---
  const handleDownloadSingle = async (id, name) => {
    try {
      setLoading(true);

      // Call API function
      const response = await downloadPayrollSingle(id);

      // 1. Ubah type menjadi 'application/pdf'
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // 2. Ubah ekstensi file menjadi .pdf
      link.setAttribute("download", `Payslip-${name}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      swal.fire(tr("shared:success_2"), tr("payroll:download_process_started"), "success");
    } catch (error) {
      console.error(error);
      swal.fire(tr("shared:error"), tr("payroll:failed_to_download_payslip"), "error");
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollDetail = async (payrollId) => {
    setLoadingDetail(true);
    try {
      const response = await getPayrollDetail(payrollId);

      if (response.status === 200) {
        const data = response.data;

        // Set form values
        methods.reset({
          payroll_name: data.payroll_name,
          from_date: data.date_from,
          to_date: data.date_to,
          schema_option: data.schema_option || "default",
          payroll_template_id: data.payroll_template_id,
          approved_by: data.approved_by || "",
          overtime_from_excess_hours: data.overtime_from_excess_hours || false,
        });

        // Set schema option
        setSchemaOption(data.schema_option || "default");
        // Set payroll data
        setPayrollData(data.payroll_data || []);
        // Set assigned items
        setAssignedItems(data.assigned_items || []);
        // Set status
        setPayrollStatus(data.status); // 'draft' atau 'published'
        // Set edit mode
        setIsEditMode(data.status === "draft");
        // Show results
        setShowResults(true);
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:error"),
        text: tr("payroll:failed_to_load_payroll_detail"),
        icon: "error",
      });
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleProcess = async () => {
    // Prevent recalculate if published
    if (payrollStatus === "published") {
      swal.fire({
        title: tr("payroll:not_allowed"),
        text: tr("payroll:cannot_recalculate_published_payroll"),
        icon: "warning",
      });
      return;
    }

    // --- TAMBAHKAN LOGIKA KONFIRMASI DISINI ---
    if (showResults) {
      const result = await swal.fire({
        title: tr("shared:are_you_sure"),
        text: tr("payroll:refreshing_will_reset_your_current_settings"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#0d6ef0",
        cancelButtonColor: "#d33",
        confirmButtonText: tr("payroll:yes_refresh_it"),
      });

      // Jika user klik cancel, hentikan proses
      if (!result.isConfirmed) {
        return;
      }
    }
    // --- END OF LOGIKA KONFIRMASI ---

    const formData = methods.getValues();

    // Validation
    if (!formData.payroll_name || !formData.from_date || !formData.to_date) {
      alert(tr("payroll:please_fill_in_all_required_fields"));
      return;
    }

    setLoadingProcess(true);
    setAssignedItems([]);
    setError(null);

    try {
      // Prepare request body
      const requestBody = {
        company_id: companyID,
        payroll_name: formData.payroll_name,
        date_from: formData.from_date,
        date_to: formData.to_date,
        schema_default:
          formData.schema_option === "default"
            ? "user"
            : formData.payroll_template_id,
        approved_by_lists: formData.approved_by || "",
        overtime_from_excess_hours: !!formData.overtime_from_excess_hours,
      };

      const response = await calculatePayroll(JSON.stringify(requestBody));

      const result = response;

      if (result.status === 200) {
        // Transform API response to table format
        const transformedData = result.data.map((item, index) => ({
          id: index + 1,
          user_id: item.user_id,
          name: `${item.first_name} ${item.last_name}`,
          email: item.user_email,
          internal_id: item.internal_id || null,
          remaining_paid_leave:
            item.result_detail.variables.remaining_paid_leave,
          workingHours: item.result_detail.variables.total_attendance_hours,
          overtime:
            item.result_detail.variables.total_regular_overtime_hours +
            item.result_detail.variables.total_holiday_overtime_hours,
          total_leave: item.result_detail.variables.total_leave_days || 0,
          takeHomePay: item.take_home_pay,
          salary: item.user_salary,
          currency: item.salary_currency_symbol,
          publish_status: "draft",
          details: item.result_detail,
          raw: item, // Keep raw data for reference
        }));

        setPayrollData(transformedData);
        setSkippedWithoutSchema(result.skipped_without_schema || []);
        setShowResults(true);
      } else {
        setError(result.message || tr("payroll:failed_to_calculate_payroll"));
      }
    } catch (err) {
      console.error("Error:", err);
      setError(tr("payroll:failed_to_connect_to_server_please"));
    } finally {
      setLoadingProcess(false);
    }
  };

  const handleAssignItems = async (itemsByEmail) => {
    // Prevent assign if published
    if (payrollStatus === "published") {
      swal.fire({
        title: tr("payroll:not_allowed"),
        text: tr("payroll:cannot_assign_items_to_published_payroll"),
        icon: "warning",
      });
      return;
    }

    setLoadingProcess(true);
    setError(null);

    try {
      // Convert itemsByEmail to array format for storage
      const newAssignedItems = [];
      Object.keys(itemsByEmail).forEach((email) => {
        itemsByEmail[email].forEach((item) => {
          newAssignedItems.push({
            id: Date.now() + Math.random(),
            email: email,
            itemName: item.name,
            value: item.formula,
            type: item.type,
            operator: item.operator,
            isTaxable: item.is_taxable,
            groupName: item.group_name,
          });
        });
      });

      const formData = methods.getValues();

      // Get all affected employees (those who had assigned items before OR have new assigned items)
      const affectedEmails = new Set([
        ...assignedItems.map((item) => item.email),
        ...Object.keys(itemsByEmail),
      ]);

      // Recalculate for all affected employees
      const updatedPayrollData = await Promise.all(
        payrollData.map(async (employeeData) => {
          // Skip if this employee is not affected
          if (!affectedEmails.has(employeeData.email)) {
            return employeeData;
          }

          // Clone the schema
          const updatedSchema = JSON.parse(
            JSON.stringify(employeeData.raw.schema_used),
          );

          // CLEAN UP: Remove previously assigned items for this employee from schema
          const previouslyAssignedForEmployee = assignedItems.filter(
            (item) => item.email === employeeData.email,
          );

          previouslyAssignedForEmployee.forEach((prevItem) => {
            const itemIndex = updatedSchema.payroll_items.findIndex(
              (schemaItem) => schemaItem.name === prevItem.itemName,
            );

            if (itemIndex !== -1) {
              const removedItem = updatedSchema.payroll_items[itemIndex];
              const groupId = removedItem.group_id;

              updatedSchema.payroll_items.splice(itemIndex, 1);

              const remainingItemsInGroup = updatedSchema.payroll_items.filter(
                (item) => item.group_id === groupId,
              );

              if (remainingItemsInGroup.length === 0) {
                const groupIndex = updatedSchema.payroll_groups.findIndex(
                  (g) => g.id === groupId,
                );
                if (groupIndex !== -1) {
                  updatedSchema.payroll_groups.splice(groupIndex, 1);
                }
              }
            }
          });

          // Add new assigned items (if any for this employee)
          const employeeAssignedItems = itemsByEmail[employeeData.email];

          if (employeeAssignedItems && employeeAssignedItems.length > 0) {
            employeeAssignedItems.forEach((item) => {
              let targetGroup = updatedSchema.payroll_groups.find(
                (g) => g.name === item.group_name,
              );

              if (!targetGroup) {
                const newGroupId = Date.now() + Math.random();
                targetGroup = {
                  id: newGroupId,
                  name: item.group_name,
                  operator: item.type === "deduction" ? "subtract" : "add",
                };
                updatedSchema.payroll_groups.push(targetGroup);
              }

              const newItem = {
                id: Date.now() + Math.random(),
                name: item.name,
                type: item.type,
                formula: item.formula,
                operator: item.operator,
                is_taxable: item.is_taxable,
                group_id: targetGroup.id,
              };

              updatedSchema.payroll_items.push(newItem);
            });
          }

          // Recalculate for this employee
          const recalcRequest = {
            company_id: companyID,
            payroll_name: formData.payroll_name,
            date_from: formData.from_date,
            date_to: formData.to_date,
            schema_default: JSON.stringify(updatedSchema),
            approved_by_lists: formData.approved_by || "",
            specific_user_id: employeeData.user_id,
            overtime_from_excess_hours: !!formData.overtime_from_excess_hours,
          };

          const response = await calculatePayroll(
            JSON.stringify(recalcRequest),
          );

          const result = response;

          if (result.status === 200 && result.data.length > 0) {
            const recalculatedData = result.data[0];

            return {
              ...employeeData,
              workingHours:
                recalculatedData.result_detail.variables.total_attendance_hours,
              overtime:
                recalculatedData.result_detail.variables
                  .total_regular_overtime_hours +
                recalculatedData.result_detail.variables
                  .total_holiday_overtime_hours,
              total_leave:
                recalculatedData.result_detail.variables.total_leave_days || 0,
              takeHomePay: recalculatedData.take_home_pay,
              details: recalculatedData.result_detail,
              raw: recalculatedData,
            };
          }

          return employeeData;
        }),
      );

      // Update state AFTER all recalculations
      setAssignedItems(newAssignedItems);
      setPayrollData(updatedPayrollData);
      setShowAssignModal(false);

      swal.fire({
        title: tr("shared:success_2"),
        text: tr("payroll:items_assigned_and_payroll_recalculated_successf"),
        icon: "success",
      });
    } catch (err) {
      console.error("Error:", err);
      setError(tr("payroll:failed_to_assign_items_and_recalculate"));
      swal.fire({
        title: tr("shared:error"),
        text: tr("payroll:failed_to_assign_items_please_try"),
        icon: "error",
      });
    } finally {
      setLoadingProcess(false);
    }
  };

  const handleDelete = (id) => {
    if (payrollStatus === "published") {
      swal.fire({
        title: tr("payroll:not_allowed"),
        text: tr("payroll:cannot_delete_employee_from_published_payroll"),
        icon: "warning",
      });
      return;
    }

    setPayrollData(payrollData.filter((item) => item.id !== id));
    setExpandedRows(expandedRows.filter((rowId) => rowId !== id));

    // Remove assigned items for this employee
    const deletedEmployee = payrollData.find((item) => item.id === id);
    if (deletedEmployee) {
      setAssignedItems(
        assignedItems.filter((item) => item.email !== deletedEmployee.email),
      );
    }
  };

  const toggleDetail = (id) => {
    if (expandedRows.includes(id)) {
      setExpandedRows(expandedRows.filter((rowId) => rowId !== id));
    } else {
      setExpandedRows([...expandedRows, id]);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleSaveDraft = async () => {
    const formData = methods.getValues();

    try {
      setLoading(true);

      const response = await savePayroll({
        ...formData,
        payrollData,
        assignedItems,
        company_id: companyID,
        status: "draft",
        payroll_id: id || null,
      });

      if (response.status == 200) {
        swal.fire({
          title: tr("shared:success_2"),
          text: response.message,
          icon: "success",
        });

        if (!id) {
          navigate("/payroll/" + response.payroll_master_id);
        } else {
          // Jika update, reload data
          loadPayrollDetail(id);
        }
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: tr("payroll:something_wrong_try_again_later"),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: tr("payroll:something_wrong_try_again_later"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    // Prevent publish if already published
    if (payrollStatus === "published") {
      swal.fire({
        title: tr("payroll:not_allowed"),
        text: tr("payroll:this_payroll_is_already_published"),
        icon: "warning",
      });
      return;
    }

    const result = await swal.fire({
      title: tr("shared:are_you_sure"),
      text: tr("payroll:you_won_t_be_able_to"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6ef0",
      cancelButtonColor: "#d33",
      confirmButtonText: tr("payroll:yes_publish_it"),
    });

    // Jika user klik cancel, hentikan proses
    if (!result.isConfirmed) {
      return;
    }

    const formData = methods.getValues();

    try {
      setLoading(true);

      const response = await savePayroll({
        ...formData,
        payrollData,
        assignedItems,
        company_id: companyID,
        status: "published",
        payroll_id: id || null,
      });

      if (response.status == 200) {
        swal.fire({
          title: tr("shared:success_2"),
          text: tr("payroll:payroll_published_successfully"),
          icon: "success",
        });

        // Reload data to update status
        if (id) {
          loadPayrollDetail(id);
        } else {
          navigate("/payroll/" + response.payroll_master_id);
        }
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: tr("payroll:something_wrong_try_again_later"),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: tr("payroll:something_wrong_try_again_later"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Publish payslip for a single employee only — sends the payslip email and
  // in-app notification to that one person, without publishing (or notifying)
  // everyone else on this payroll.
  const handlePublishSingle = async (item) => {
    if (item.publish_status === "published") {
      return;
    }

    const result = await swal.fire({
      title: tr("payroll:publish_payslip_for_name", { name: item.name }),
      text: tr("payroll:this_will_send_a_payslip_email"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d6ef0",
      cancelButtonColor: "#d33",
      confirmButtonText: tr("payroll:yes_publish_it"),
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setLoadingProcess(true);

      const response = await publishPayrollSingle(item.id);

      if (response.status === 200) {
        setPayrollData((prev) =>
          prev.map((row) =>
            row.id === item.id ? { ...row, publish_status: "published" } : row,
          ),
        );

        swal.fire({
          title: tr("shared:success_2"),
          text: tr("payroll:payslip_published_and_notification_sent"),
          icon: "success",
        });
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: response.message || tr("payroll:failed_to_publish_payslip"),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: tr("payroll:failed_to_publish_payslip_please_try"),
        icon: "error",
      });
    } finally {
      setLoadingProcess(false);
    }
  };

  // Handler untuk export summary only
  const handleExportSummary = () => {
    const formData = methods.getValues();

    try {
      const result = PayrollExcelExporter.exportSummaryOnly(payrollData, {
        payroll_name: formData.payroll_name,
        date_from: formData.from_date,
        date_to: formData.to_date,
      });

      if (result.success) {
        swal.fire({
          title: tr("shared:success_2"),
          text: tr("payroll:summary_exported_filename", { filename: result.filename }),
          icon: "success",
        });
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: tr("payroll:export_failed_error", { error: result.error }),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: `Export failed`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler untuk export full detail
  const handleExportFullDetail = () => {
    const formData = methods.getValues();

    try {
      const result = PayrollExcelExporter.exportToExcel(payrollData, {
        payroll_name: formData.payroll_name,
        date_from: formData.from_date,
        date_to: formData.to_date,
      });

      if (result.success) {
        swal.fire({
          title: tr("shared:success_2"),
          text: tr("payroll:full_detail_exported_filename", { filename: result.filename }),
          icon: "success",
        });
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: tr("payroll:export_failed_error", { error: result.error }),
          icon: "error",
        });
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: `Export failed`,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <div
        style={{
          paddingBottom: "100px",
        }}
      >
        <div
          className="container"
          style={{ maxWidth: "1200px", margin: "30px auto" }}
        >
          {/* Input Section */}
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              marginBottom: "20px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* Card Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #007DFF 0%, #0056b3 100%)",
                padding: "20px 28px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h5 className="mb-0 text-white fw-bold">
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  {id
                    ? payrollStatus === "published"
                      ? tr("payroll:view_payroll")
                      : tr("payroll:edit_payroll")
                    : tr("payroll:create_new_payroll")}
                </h5>
                <small className="text-white-50">
                  {id ? tr("payroll:review_or_edit_payroll_details_below") : tr("payroll:fill_in_the_details_to_generate")}
                </small>
              </div>
              {payrollStatus && (
                <span
                  className={`badge fs-6 px-3 py-2 ${
                    payrollStatus === "published" ? "bg-success" : "bg-warning text-dark"
                  }`}
                >
                  {payrollStatus.toUpperCase()}
                </span>
              )}
            </div>

            {/* Card Body */}
            <div style={{ padding: "28px" }}>
            {loadingDetail ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{tr("payroll:loading")}</span>
                </div>
                <p className="text-muted mt-3">{tr("payroll:loading_payroll_details")}</p>
              </div>
            ) : (
              <>
                {/* Payroll Name */}
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-1">
                    <i className="fas fa-tag me-1 text-primary"></i>
                    {tr("shared:payroll_name")}
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder={tr("payroll:eg_payroll_november_2025")}
                    {...methods.register("payroll_name")}
                    disabled={payrollStatus === "published"}
                  />
                </div>

                {/* Date Range */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      <i className="fas fa-calendar-alt me-1 text-primary"></i>
                      {tr("payroll:from_date")}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      {...methods.register("from_date")}
                      disabled={payrollStatus === "published"}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold mb-1">
                      <i className="fas fa-calendar-check me-1 text-primary"></i>
                      {tr("payroll:to_date")}
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      {...methods.register("to_date")}
                      disabled={payrollStatus === "published"}
                    />
                  </div>
                </div>

                {/* Payroll Schema */}
                <div className="mb-4">
                  <label className="form-label fw-semibold mb-2">
                    <i className="fas fa-layer-group me-1 text-primary"></i>
                    {tr("shared:payroll_schema")}
                  </label>
                  <div className="d-flex flex-column flex-sm-row gap-3">
                    <div
                      className={`d-flex align-items-center gap-2 px-4 py-3 rounded-3 border cursor-pointer ${
                        schemaOption === "default"
                          ? "border-primary bg-primary bg-opacity-10"
                          : "border-secondary bg-light"
                      }`}
                      style={{ cursor: payrollStatus === "published" ? "not-allowed" : "pointer", flex: 1 }}
                      onClick={() => {
                        if (payrollStatus !== "published") {
                          setSchemaOption("default");
                          methods.setValue("schema_option", "default");
                        }
                      }}
                    >
                      <input
                        className="form-check-input mt-0"
                        type="radio"
                        name="schemaOption"
                        id="defaultSchema"
                        value="default"
                        checked={schemaOption === "default"}
                        onChange={(e) => {
                          setSchemaOption(e.target.value);
                          methods.setValue("schema_option", e.target.value);
                        }}
                        disabled={payrollStatus === "published"}
                      />
                      <div>
                        <div className="fw-semibold">{tr("payroll:default_schema")}</div>
                        <small className="text-muted">{tr("payroll:use_each_user_s_assigned_payroll")}</small>
                      </div>
                    </div>
                    <div
                      className={`d-flex align-items-center gap-2 px-4 py-3 rounded-3 border ${
                        schemaOption === "template"
                          ? "border-primary bg-primary bg-opacity-10"
                          : "border-secondary bg-light"
                      }`}
                      style={{ cursor: payrollStatus === "published" ? "not-allowed" : "pointer", flex: 1 }}
                      onClick={() => {
                        if (payrollStatus !== "published") {
                          setSchemaOption("template");
                          methods.setValue("schema_option", "template");
                        }
                      }}
                    >
                      <input
                        className="form-check-input mt-0"
                        type="radio"
                        name="schemaOption"
                        id="templateSchema"
                        value="template"
                        checked={schemaOption === "template"}
                        onChange={(e) => {
                          setSchemaOption(e.target.value);
                          methods.setValue("schema_option", e.target.value);
                        }}
                        disabled={payrollStatus === "published"}
                      />
                      <div>
                        <div className="fw-semibold">{tr("payroll:from_template")}</div>
                        <small className="text-muted">{tr("payroll:apply_a_specific_payroll_template")}</small>
                      </div>
                    </div>
                  </div>

                  {schemaOption === "template" && (
                    <div style={{ marginTop: "12px" }}>
                      <SelectPayrollTemplate
                        name="payroll_template_id"
                        label={tr("payroll:select_template")}
                        placeholder={tr("payroll:choose_template")}
                        disabled={payrollStatus === "published"}
                        defaultValue={methods.watch("payroll_template_id")}
                      />
                    </div>
                  )}
                </div>

                {/* Overtime Source */}
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="overtimeFromExcessHours"
                      {...methods.register("overtime_from_excess_hours")}
                      disabled={payrollStatus === "published"}
                    />
                    <label
                      className="form-check-label fw-semibold"
                      htmlFor="overtimeFromExcessHours"
                    >
                      <i className="fas fa-clock me-1 text-primary"></i>
                      {tr("payroll:treat_excess_working_hours_as_automatic")}
                    </label>
                  </div>
                  <small className="text-muted">
                    {tr("payroll:when_checked_the_difference_between_actual")}
                  </small>
                </div>

                {/* Approved By */}
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    <i className="fas fa-user-check me-1 text-primary"></i>
                    {tr("payroll:approved_by")}
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder={tr("payroll:eg_finance_manager_john_doe_account")}
                    {...methods.register("approved_by")}
                    disabled={payrollStatus === "published"}
                  ></textarea>
                  <small className="text-muted">{tr("payroll:names_to_appear_on_the_payslip")}</small>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {skippedWithoutSchema.length > 0 && (
                  <div className="alert alert-warning" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    <strong>
                      {tr("payroll:skipped_without_schema_title", { count: skippedWithoutSchema.length })}
                    </strong>
                    <div className="small mt-1">
                      {skippedWithoutSchema
                        .map((item) => `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.user_email)
                        .join(", ")}
                    </div>
                    <div className="small mt-1">{tr("payroll:skipped_without_schema_hint")}</div>
                  </div>
                )}

                {payrollStatus !== "published" && (
                  <div className="text-end mt-2">
                    <button
                      className="btn btn-primary px-4"
                      onClick={handleProcess}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {tr("shared:processing")}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sync-alt me-2"></i>
                          {showResults ? tr("payroll:refresh_payroll") : tr("payroll:process_payroll")}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
            </div>
          </div>

          {/* Results Section */}
          {showResults && (
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                padding: "25px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <i className="fas fa-table me-2"></i>
                  {tr("payroll:results")}{payrollData.length} {" "}{tr("payroll:employees")}
                </h5>
                <div className="d-flex gap-2">
                  {/* Tombol Assign - Hanya tampil jika DRAFT */}
                  {payrollStatus !== "published" && (
                    <button
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center shadow-sm position-relative"
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "6px",
                        padding: "6px 12px",
                      }}
                      onClick={() => setShowAssignModal(true)}
                      disabled={loading}
                    >
                      <ArrowRight size={14} className="me-2" />
                      {tr("payroll:assign_items")}
                      {assignedItems.length > 0 && (
                        <span
                          className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary"
                          style={{
                            fontSize: "10px",
                            padding: "3px 6px",
                          }}
                        >
                          {assignedItems.length}
                        </span>
                      )}
                    </button>
                  )}

                  {/* Tombol Export - Ada 2 opsi */}
                  <div className="d-flex gap-2">
                    {/* Button Export Summary */}
                    <button
                      className="btn btn-outline-success btn-sm d-flex align-items-center shadow-sm"
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "6px",
                        padding: "6px 12px",
                      }}
                      onClick={handleExportSummary}
                      disabled={loading || payrollData.length === 0}
                      title={tr("payroll:export_summary_only_1_sheet")}
                    >
                      <FileDown size={14} className="me-2" />
                      {tr("payroll:export_summary")}
                    </button>

                    {/* Button Export Full Detail */}
                    <button
                      className="btn btn-outline-success btn-sm d-flex align-items-center shadow-sm"
                      style={{
                        fontSize: "12px",
                        fontWeight: "500",
                        borderRadius: "6px",
                        padding: "6px 12px",
                      }}
                      onClick={handleExportFullDetail}
                      disabled={loading || payrollData.length === 0}
                      title={tr("payroll:export_full_detail_5_sheets")}
                    >
                      <FileDown size={14} className="me-2" />
                      {tr("payroll:export_full_detail")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead style={{ backgroundColor: "#e9ecef" }}>
                    <tr>
                      <th>{tr("shared:no")}</th>
                      <th>{tr("shared:name")}</th>
                      <th>{tr("shared:email")}</th>
                      <th>{tr("common:breadcrumb.salary")}</th>
                      <th>{tr("payroll:w_hours")}</th>
                      <th>{tr("payroll:t_overtime")}</th>
                      <th>{tr("payroll:t_leave")}</th>
                      <th>{tr("payroll:take_home_pay")}</th>
                      <th>{tr("payroll:action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollData.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <tr>
                          <td>{index + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.email}</td>
                          <td nowrap="true">
                            <strong>
                              {item.currency} {formatCurrency(item.salary)}
                            </strong>
                          </td>
                          <td>{formatCurrency(item.workingHours)}</td>
                          <td>{formatCurrency(item.overtime)}</td>
                          <td>{item.total_leave}</td>
                          <td nowrap="true">
                            <strong>
                              {item.currency} {formatCurrency(item.takeHomePay)}
                            </strong>
                          </td>
                          <td className="align-middle text-nowrap">
                            <div className="d-flex justify-content-center align-items-center gap-2">
                              {/* Button Detail */}
                              <button
                                className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
                                style={{
                                  color: "#333", // Warna teks lebih soft dari hitam pekat
                                  fontSize: "13px",
                                  fontWeight: "500",
                                }}
                                onClick={() => toggleDetail(item.id)}
                              >
                                {expandedRows.includes(item.id) ? (
                                  <EyeOff size={16} className="me-2" />
                                ) : (
                                  <Eye size={16} className="me-2" />
                                )}
                                {expandedRows.includes(item.id)
                                  ? tr("payroll:hide")
                                  : tr("common:breadcrumb.detail")}
                              </button>

                              {payrollStatus == "published" && (
                                <button
                                  onClick={() =>
                                    handleDownloadSingle(item.id, item.name)
                                  }
                                  className="btn btn-sm btn-light"
                                  style={{
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faDownload}
                                    className="me-1"
                                  />
                                  {tr("payroll:payslip")}
                                </button>
                              )}

                              {/* Button Publish per karyawan - cuma untuk payroll
                                  yang sudah tersimpan (ada id asli dari DB) dan
                                  belum published (baik master maupun item ini) */}
                              {id && payrollStatus !== "published" && (
                                <>
                                  <div
                                    className="vr opacity-25"
                                    style={{
                                      height: "16px",
                                      backgroundColor: "#ccc",
                                    }}
                                  ></div>
                                  {item.publish_status === "published" ? (
                                    <span
                                      className="badge bg-success-subtle text-success"
                                      style={{ fontSize: "11px" }}
                                    >
                                      <i className="fas fa-check-circle me-1"></i>
                                      {tr("payroll:published")}
                                    </span>
                                  ) : (
                                    <button
                                      className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
                                      style={{
                                        color: "#0d6efd",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                      }}
                                      onClick={() => handlePublishSingle(item)}
                                      disabled={loading}
                                    >
                                      <i className="fas fa-paper-plane me-2"></i>
                                      {tr("payroll:publish_2")}
                                    </button>
                                  )}
                                </>
                              )}

                              {/* Button Delete - Hanya tampil jika DRAFT */}
                              {payrollStatus !== "published" && (
                                <>
                                  {/* Vertical Divider */}
                                  <div
                                    className="vr opacity-25"
                                    style={{
                                      height: "16px",
                                      backgroundColor: "#ccc",
                                    }}
                                  ></div>

                                  {/* Button Delete */}
                                  <button
                                    className="btn btn-link text-decoration-none p-0 d-flex align-items-center"
                                    style={{
                                      color: "#dc3545",
                                      fontSize: "13px",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <Trash2 size={16} className="me-2" />
                                    {tr("common:actions.delete")}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedRows.includes(item.id) && (
                          <tr style={{ backgroundColor: "#f8f9fa" }}>
                            <td colSpan="9">
                              <div style={{ padding: "20px" }}>
                                <h6 className="mb-3">
                                  <i className="fas fa-calculator me-2"></i>
                                  {tr("payroll:calculation_details")}{" "} {item.name}
                                </h6>

                                {/* Payroll Period Information */}
                                <div className="mb-4">
                                  <div className="alert alert-primary d-flex justify-content-between align-items-center">
                                    <div>
                                      <i className="fas fa-calendar-alt me-2"></i>
                                      <strong>{tr("payroll:payroll_period")}</strong>{" "}
                                      {item.raw.date_from} {" "}{tr("payroll:to")}{" "} {item.raw.date_to}
                                    </div>
                                    <div>
                                      <i className="fas fa-file-invoice me-2"></i>
                                      <strong>{tr("payroll:payroll_name")}</strong>{" "}
                                      {item.raw.payroll_name}
                                    </div>
                                  </div>
                                </div>

                                {/* Schema Information Section */}
                                <div className="mb-4">
                                  <h6 className="text-muted mb-3">
                                    <i className="fas fa-cog me-2"></i>
                                    {tr("payroll:payroll_schema_used")}
                                  </h6>
                                  <div
                                    className="card"
                                    style={{
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #dee2e6",
                                    }}
                                  >
                                    <div className="card-body">
                                      <div className="row">
                                        <div className="col-md-6">
                                          <small className="text-muted">
                                            {tr("payroll:template_name")}
                                          </small>
                                          <div className="fw-semibold mb-3">
                                            {
                                              item.raw.schema_used.settings
                                                .template_name
                                            }
                                          </div>

                                          <small className="text-muted">
                                            {tr("payroll:attendance_type")}
                                          </small>
                                          <div className="fw-semibold mb-3">
                                            <span className="badge bg-info text-capitalize">
                                              {
                                                item.raw.schema_used.settings
                                                  .attendance_type
                                              }
                                            </span>
                                          </div>

                                          <small className="text-muted">
                                            {tr("payroll:break_time")}
                                          </small>
                                          <div className="fw-semibold mb-3">
                                            {
                                              item.raw.schema_used.settings
                                                .break_time_hours
                                            }{" "}
                                            {tr("payroll:hour_s")}
                                            <span
                                              className="text-muted ms-2"
                                              style={{ fontSize: "0.9em" }}
                                            >
                                              {tr("payroll:applied_to")}{" "}
                                              {
                                                item.raw.schema_used.settings
                                                  .break_time_apply_to
                                              }
                                              )
                                            </span>
                                          </div>
                                        </div>

                                        <div className="col-md-6">
                                          <small className="text-muted">
                                            {tr("payroll:overtime_schema")}
                                          </small>
                                          <div className="fw-semibold mb-2">
                                            {tr("payroll:regular")}{" "}
                                            <span className="badge bg-secondary text-capitalize">
                                              {
                                                item.raw.schema_used.settings
                                                  .overtime_regular_schema
                                              }
                                            </span>
                                          </div>
                                          <div className="fw-semibold mb-3">
                                            {tr("payroll:holiday")}{" "}
                                            <span className="badge bg-secondary text-capitalize">
                                              {
                                                item.raw.schema_used.settings
                                                  .overtime_holiday_schema
                                              }
                                            </span>
                                          </div>

                                          <small className="text-muted">
                                            {tr("payroll:tolerance_minutes")}
                                          </small>
                                          <div className="fw-semibold mb-3">
                                            {
                                              item.raw.schema_used.settings
                                                .tolerance_minutes
                                            }{" "}
                                            {tr("payroll:minutes")}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Overtime Multiplier Rules Detail */}
                                <div className="mb-4">
                                  <h6 className="text-muted mb-3">
                                    <i className="fas fa-clock me-2"></i>
                                    {tr("payroll:overtime_multiplier_rules")}
                                  </h6>
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div
                                        className="card"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          className="card-header"
                                          style={{ backgroundColor: "#e7f3ff" }}
                                        >
                                          <strong>
                                            <i className="fas fa-sun me-2"></i>
                                            {tr("payroll:regular_day_overtime")}
                                          </strong>
                                        </div>
                                        <div className="card-body">
                                          {item.raw.schema_used.settings
                                            .overtime_regular_fixed_sequence
                                            ?.length > 0 ? (
                                            item.raw.schema_used.settings.overtime_regular_fixed_sequence.map(
                                              (rule, idx) => (
                                                <div
                                                  key={idx}
                                                  className="d-flex justify-content-between align-items-center mb-2 pb-2"
                                                  style={{
                                                    borderBottom:
                                                      idx <
                                                      item.raw.schema_used
                                                        .settings
                                                        .overtime_regular_fixed_sequence
                                                        .length -
                                                        1
                                                        ? "1px solid #eee"
                                                        : "none",
                                                  }}
                                                >
                                                  <span>
                                                    <i className="fas fa-arrow-right me-2 text-muted"></i>
                                                    {tr("payroll:hour")}{" "} {rule.hour}
                                                  </span>
                                                  <span className="badge bg-success">
                                                    {rule.multiplier}x
                                                  </span>
                                                </div>
                                              ),
                                            )
                                          ) : (
                                            <div className="text-muted">
                                              {tr("payroll:flat_multiplier")}{" "}
                                              {
                                                item.raw.schema_used.settings
                                                  .regular_flat_multiplier
                                              }
                                              x
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-md-6">
                                      <div
                                        className="card"
                                        style={{ border: "1px solid #dee2e6" }}
                                      >
                                        <div
                                          className="card-header"
                                          style={{ backgroundColor: "#fff3e0" }}
                                        >
                                          <strong>
                                            <i className="fas fa-moon me-2"></i>
                                            {tr("payroll:holiday_overtime")}
                                          </strong>
                                        </div>
                                        <div className="card-body">
                                          {item.raw.schema_used.settings
                                            .overtime_holiday_fixed_sequence
                                            ?.length > 0 ? (
                                            item.raw.schema_used.settings.overtime_holiday_fixed_sequence.map(
                                              (rule, idx) => (
                                                <div
                                                  key={idx}
                                                  className="d-flex justify-content-between align-items-center mb-2 pb-2"
                                                  style={{
                                                    borderBottom:
                                                      idx <
                                                      item.raw.schema_used
                                                        .settings
                                                        .overtime_holiday_fixed_sequence
                                                        .length -
                                                        1
                                                        ? "1px solid #eee"
                                                        : "none",
                                                  }}
                                                >
                                                  <span>
                                                    <i className="fas fa-arrow-right me-2 text-muted"></i>
                                                    {rule.type === "single"
                                                      ? `Hour ${rule.hour}`
                                                      : `Hours ${rule.from}-${rule.to}`}
                                                  </span>
                                                  <span className="badge bg-danger">
                                                    {rule.multiplier}x
                                                  </span>
                                                </div>
                                              ),
                                            )
                                          ) : (
                                            <div className="text-muted">
                                              {tr("payroll:flat_multiplier")}{" "}
                                              {
                                                item.raw.schema_used.settings
                                                  .holiday_flat_multiplier
                                              }
                                              x
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Approved By Section */}
                                {item.raw.approved_by && (
                                  <div className="mb-4">
                                    <h6 className="text-muted mb-3">
                                      <i className="fas fa-user-check me-2"></i>
                                      {tr("payroll:approved_by")}
                                    </h6>
                                    <div className="alert alert-info mb-0">
                                      <i className="fas fa-info-circle me-2"></i>
                                      {item.raw.approved_by}
                                    </div>
                                  </div>
                                )}

                                {/* Variables Section */}
                                <div className="mb-4">
                                  <h6 className="text-muted mb-3">
                                    <i className="fas fa-info-circle me-2"></i>
                                    {tr("payroll:calculation_variables")}
                                  </h6>
                                  <div className="row">
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:base_salary")}
                                      </small>
                                      <div className="fw-semibold">
                                        {item.currency}{" "}
                                        {formatCurrency(
                                          item.details.variables
                                            .employee_salary,
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:working_days")}
                                      </small>
                                      <div className="fw-semibold">
                                        {item.details.variables.working_days}{" "}
                                        {tr("shared:days")}
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:attendance_days")}
                                      </small>
                                      <div className="fw-semibold">
                                        {
                                          item.details.variables
                                            .total_attendance_days
                                        }{" "}
                                        {tr("shared:days")}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="row mt-3">
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:hourly_rate")}
                                      </small>
                                      <div className="fw-semibold">
                                        {item.currency}{" "}
                                        {formatCurrency(
                                          item.details.variables
                                            .salary_hourly_rate,
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:daily_rate")}
                                      </small>
                                      <div className="fw-semibold">
                                        {item.currency}{" "}
                                        {formatCurrency(
                                          item.details.variables
                                            .salary_daily_rate,
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-4">
                                      <small className="text-muted">
                                        {tr("payroll:late_minutes")}
                                      </small>
                                      <div className="fw-semibold text-danger">
                                        {item.details.variables.late_minutes}{" "}
                                        {tr("payroll:minutes")}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Groups Section */}
                                {item.details.groups.map((group, gIdx) => (
                                  <div key={gIdx} className="mb-4">
                                    <h6 className="text-muted mb-3">
                                      {/* Icon diubah menjadi folder saja, karena plus/minus sudah tidak relevan di level group */}
                                      <i className="fas fa-folder-open me-2 text-primary"></i>
                                      {group.group.name}
                                    </h6>

                                    <div className="row">
                                      {group.items.map(
                                        (payItem, iIdx) =>
                                          payItem.calculated_value !== 0 && (
                                            <div
                                              key={iIdx}
                                              className="col-md-6"
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  padding: "8px 0",
                                                  borderBottom:
                                                    "1px solid #dee2e6",
                                                }}
                                              >
                                                <span>{payItem.name}</span>
                                                <span
                                                  className={`fw-semibold ${
                                                    // Warna teks murni ditentukan oleh operator item
                                                    payItem.operator ===
                                                    "subtract"
                                                      ? "text-danger"
                                                      : "text-success"
                                                  }`}
                                                >
                                                  {/* Tambahkan tanda minus secara manual hanya jika operatornya subtract */}
                                                  {payItem.operator ===
                                                  "subtract"
                                                    ? "- "
                                                    : "+ "}
                                                  {item.currency}{" "}
                                                  {formatCurrency(
                                                    payItem.calculated_value,
                                                  )}
                                                </span>
                                              </div>
                                            </div>
                                          ),
                                      )}
                                    </div>

                                    <div className="row mt-2">
                                      <div className="col-12">
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            padding: "10px",
                                            // Latar belakang mengikuti hasil akhir angka (subtotal)
                                            backgroundColor:
                                              group.subtotal >= 0
                                                ? "#d1e7dd"
                                                : "#f8d7da",
                                            borderRadius: "4px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          <span>
                                            {tr("payroll:subtotal")}{" "} {group.group.name}
                                          </span>
                                          <span
                                            className={
                                              // Warna teks mengikuti hasil akhir angka (subtotal)
                                              group.subtotal >= 0
                                                ? "text-success"
                                                : "text-danger"
                                            }
                                          >
                                            {/* Tanda minus/plus sudah otomatis di-handle oleh nilai subtotal di formatCurrency */}
                                            {item.currency}{" "}
                                            {formatCurrency(group.subtotal)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Summary */}
                                <div className="row mt-4">
                                  <div className="col-md-12">
                                    <div
                                      style={{
                                        padding: "15px",
                                        backgroundColor: "#0d6efd",
                                        color: "white",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <div className="d-flex justify-content-between mb-2">
                                        <span className="fw-bold">
                                          {tr("payroll:gross_salary")}
                                        </span>
                                        <span className="fw-bold">
                                          {item.currency}{" "}
                                          {formatCurrency(
                                            item.details.gross_salary,
                                          )}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between mb-2">
                                        <span className="fw-bold">
                                          {tr("shared:total_deductions")}
                                        </span>
                                        <span className="fw-bold">
                                          - {item.currency}{" "}
                                          {formatCurrency(
                                            item.details.total_deductions,
                                          )}
                                        </span>
                                      </div>
                                      <hr
                                        style={{
                                          borderColor: "rgba(255,255,255,0.3)",
                                          margin: "10px 0",
                                        }}
                                      />
                                      <div className="d-flex justify-content-between">
                                        <span
                                          className="fw-bold"
                                          style={{ fontSize: "1.2em" }}
                                        >
                                          {tr("payroll:net_take_home_pay")}
                                        </span>
                                        <span
                                          className="fw-bold"
                                          style={{ fontSize: "1.2em" }}
                                        >
                                          {item.currency}{" "}
                                          {formatCurrency(item.takeHomePay)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom Actions - Hanya tampil jika ada results */}
        {showResults && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              right: 0,
              left: 0,
              zIndex: 1000,
            }}
          >
            <div
              className="container"
              style={{ maxWidth: "1200px", margin: "0 auto" }}
            >
              <div className="text-end">
                {payrollStatus !== "published" && (
                  <>
                    <button
                      className="btn btn-secondary no-radius"
                      style={{ padding: "10px 25px" }}
                      onClick={handleSaveDraft}
                    >
                      <i className="fas fa-save me-2"></i>
                      {tr("payroll:save_as_draft")}
                    </button>
                    <button
                      className="btn btn-primary no-radius"
                      style={{ padding: "10px 25px" }}
                      onClick={handlePublish}
                    >
                      <i className="fas fa-paper-plane me-2"></i>
                      {tr("payroll:publish")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assign Items Modal */}
      <ModalAssignItemsPayroll
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        payrollData={payrollData}
        assignedItems={assignedItems}
        onSubmit={handleAssignItems}
        onDeleteItem={(itemId) => {
          // Only update local state, don't trigger recalculation yet
          setAssignedItems(assignedItems.filter((item) => item.id !== itemId));
        }}
      />
    </FormProvider>
  );
}
